import { LEAD_FINDER_BULK_TEMPLATE, LEAD_FINDER_DISMISSED_BULK_TEMPLATE } from '../../bulk-action-templates';
import type { ListBuilderColumn, ListBuilderConfiguration, ListBuilderList } from '../../types';

/**
 * Sales > Lead Finder (PD-16825) — pharmacies registered with a state board
 * that are not yet this tenant's clients or leads, one row per physical
 * location.
 *
 * Rows come from `GET /lead-finder/locations` (crm-api lead-finder module),
 * which reads workd_mission_control.lm_lead_location cross-database. Like the
 * Registry list, `objectTable: 'lm_lead_location'` only scopes the field and
 * filter configs; it is not a TypeORM entity. Unlike the Registry lookup this
 * list pages and has tabs: it is a gated sales surface (Compliance capability
 * + lead_finder:search) with its own per-tenant rate limit.
 *
 * Tabs are lists of the same config: "New pharmacies" (issued within the
 * last 30 days, newest first), "All pharmacies", and "Dismissed" (the
 * locations this account dismissed as not a fit, where they can be restored;
 * every other tab leaves them out). The issued-within window
 * is a default, not a lock, so a rep can widen or narrow it.
 */

const OBJ = 'lm_lead_location';

const pharmacyColumn: ListBuilderColumn = {
	id: 'lead-finder-pharmacy',
	fields: [
		{
			id: 'pharmacy-name-field',
			dataConfig: { type: 'field', field: { fieldName: 'name', objectTable: OBJ }, defaultValue: '(no name on record)' },
			styleConfig: { size: 'md', weight: 'medium', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		{ id: 'pharmacy-name-break', dataConfig: { type: 'custom_text', customText: '' }, displayStyle: 'line_break' },
		{
			id: 'pharmacy-legal-name-field',
			dataConfig: { type: 'field', field: { fieldName: 'legalName', objectTable: OBJ } },
			styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
			displayStyle: 'text',
		},
		{ id: 'pharmacy-legal-break', dataConfig: { type: 'custom_text', customText: '' }, displayStyle: 'line_break' },
		{
			id: 'pharmacy-address-field',
			dataConfig: { type: 'field', field: { fieldName: 'addressFull', objectTable: OBJ } },
			styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
			displayStyle: 'text',
		},
	],
	label: 'Pharmacy',
	headers: [{ id: 'h-lead-finder-pharmacy', text: 'Pharmacy' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [{ direction: 'ASC', fieldName: 'name', objectTable: OBJ }],
	minWidth: 280,
};

/** Value on top, how it was decided below ("State permit type", "NPI taxonomy", "Workd classification"). */
const labeledColumn = (id: string, label: string, valueField: string, basisField: string, sortField: string): ListBuilderColumn => ({
	id,
	fields: [
		{
			id: `${id}-value`,
			dataConfig: { type: 'field', field: { fieldName: valueField, objectTable: OBJ } },
			styleConfig: { size: 'sm', weight: 'medium', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		{ id: `${id}-break`, dataConfig: { type: 'custom_text', customText: '' }, displayStyle: 'line_break' },
		{
			id: `${id}-basis`,
			dataConfig: { type: 'field', field: { fieldName: basisField, objectTable: OBJ } },
			styleConfig: { size: 'xs', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
			displayStyle: 'text',
		},
	],
	label,
	headers: [{ id: `h-${id}`, text: label }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [{ direction: 'ASC', fieldName: sortField, objectTable: OBJ }],
	width: 170,
});

const classColumn = labeledColumn('lead-finder-class', 'Class of trade', 'classOfTradeLabel', 'classBasisLabel', 'classOfTrade');
const ownershipColumn = labeledColumn('lead-finder-ownership', 'Independent / chain', 'ownershipLabel', 'ownershipBasisLabel', 'ownership');

const licenseColumn: ListBuilderColumn = {
	id: 'lead-finder-license',
	fields: [
		{
			id: 'license-number-field',
			dataConfig: { type: 'field', field: { fieldName: 'primaryLicenseNumber', objectTable: OBJ } },
			styleConfig: { size: 'sm', weight: 'medium', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		{ id: 'license-number-break', dataConfig: { type: 'custom_text', customText: '' }, displayStyle: 'line_break' },
		{
			id: 'license-type-field',
			dataConfig: { type: 'field', field: { fieldName: 'primaryLicenseType', objectTable: OBJ } },
			styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
			displayStyle: 'text',
		},
	],
	label: 'State license',
	headers: [{ id: 'h-lead-finder-license', text: 'State license' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 160,
};

const datesColumn: ListBuilderColumn = {
	id: 'lead-finder-issued',
	// Issued on top; "found by Workd" below. A board that publishes no issue
	// dates shows an empty top line rather than an old-looking date.
	fields: [
		{
			id: 'issued-date-field',
			dataConfig: { type: 'field', field: { fieldName: 'newestIssueDate', objectTable: OBJ } },
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'date',
		},
		{ id: 'issued-date-break', dataConfig: { type: 'custom_text', customText: '' }, displayStyle: 'line_break' },
		{
			id: 'found-date-field',
			dataConfig: { type: 'field', field: { fieldName: 'foundDate', objectTable: OBJ } },
			styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
			displayStyle: 'date',
		},
	],
	label: 'Issued / Found',
	headers: [{ id: 'h-lead-finder-issued', text: 'Issued / Found' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [{ direction: 'DESC', fieldName: 'newestIssueDate', objectTable: OBJ }],
	width: 130,
};

const phoneColumn: ListBuilderColumn = {
	id: 'lead-finder-phone',
	fields: [
		{
			id: 'phone-field',
			dataConfig: { type: 'field', field: { fieldName: 'phone', objectTable: OBJ } },
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'phone',
		},
		{ id: 'phone-break', dataConfig: { type: 'custom_text', customText: '' }, displayStyle: 'line_break' },
		{
			id: 'phone-source-field',
			dataConfig: { type: 'field', field: { fieldName: 'phoneSource', objectTable: OBJ } },
			styleConfig: { size: 'xs', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
			displayStyle: 'text',
		},
	],
	label: 'Phone',
	headers: [{ id: 'h-lead-finder-phone', text: 'Phone' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 140,
};

const statusColumn: ListBuilderColumn = {
	id: 'lead-finder-status',
	// The status chip, then — for a possible or existing client — the client
	// it points at, linked to its record.
	fields: [
		{
			id: 'lead-status-field',
			dataConfig: { type: 'field', field: { fieldName: 'leadStatusLabel', objectTable: OBJ }, defaultValue: 'New' },
			displayStyle: 'badge',
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'status_map',
					statusMap: {
						New: { type: 'success' },
						'Possible existing client': { type: 'warning' },
						'Already a client': { type: 'default' },
					},
				},
			},
		},
		{ id: 'lead-status-break', dataConfig: { type: 'custom_text', customText: '' }, displayStyle: 'line_break' },
		{ id: 'dismissed-note-break', dataConfig: { type: 'custom_text', customText: '' }, displayStyle: 'line_break' },
		{
			// "Not a fit: <reason>" on a dismissed location (Dismissed tab).
			id: 'dismissed-note-field',
			dataConfig: { type: 'field', field: { fieldName: 'dismissedNote', objectTable: OBJ } },
			styleConfig: { size: 'xs', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
			displayStyle: 'text',
		},
		{ id: 'matched-client-break', dataConfig: { type: 'custom_text', customText: '' }, displayStyle: 'line_break' },
		{
			id: 'matched-client-field',
			dataConfig: { type: 'field', field: { fieldName: 'matchedClientName', objectTable: OBJ } },
			link: { enabled: true, type: 'object', objectTable: 'client', idField: 'matchedClientId' },
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	label: 'Lead status',
	headers: [{ id: 'h-lead-finder-status', text: 'Lead status' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 190,
};

const actionsColumn: ListBuilderColumn = {
	id: 'lead-finder-actions',
	fields: [
		{
			id: 'actions-dropdown',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'import-lead-action',
					label: 'Import as lead',
					iconName: 'plus',
					callbackName: 'importLead',
					variant: 'primary',
					visibility: 'visible',
					// A location whose license is already on a client is never
					// importable; the server re-checks on import.
					conditionalVisibility: { field: 'canImport', condition: 'equals', value: true },
					requiredPermission: 'lead_finder:import',
				},
				{
					// Takes the location out of every tab but Dismissed, for this account.
					id: 'dismiss-lead-action',
					label: 'Dismiss (not a fit)',
					iconName: 'ban',
					callbackName: 'dismissLead',
					variant: 'secondary',
					conditionalVisibility: { field: 'dismissed', condition: 'equals', value: false },
					requiredPermission: 'lead_finder:import',
				},
				{
					id: 'restore-lead-action',
					label: 'Restore to results',
					iconName: 'rotate-left',
					callbackName: 'restoreLead',
					variant: 'secondary',
					conditionalVisibility: { field: 'dismissed', condition: 'equals', value: true },
					requiredPermission: 'lead_finder:import',
				},
			],
		},
	],
	label: '',
	headers: [{ id: 'h-lead-finder-actions', text: '' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 64,
};

const COLUMNS: Array<ListBuilderColumn> = [
	pharmacyColumn,
	classColumn,
	ownershipColumn,
	licenseColumn,
	datesColumn,
	phoneColumn,
	statusColumn,
	actionsColumn,
];

export const LEAD_FINDER_LIST_IDS = {
	NEW_PHARMACIES: 'lead-finder-new-pharmacies',
	ALL: 'lead-finder-all',
	DISMISSED: 'lead-finder-dismissed',
} as const;

const newPharmaciesList: ListBuilderList = {
	id: LEAD_FINDER_LIST_IDS.NEW_PHARMACIES,
	cacheTTL: 300000,
	name: 'New pharmacies',
	type: 'default',
	active: true,
	sortOrder: 1,
	filterRules: {
		// 30 days, as designed. Boards are discovered about a month after issue
		// on average, but the window still holds ~180-195 new permits per
		// Compliance tenant (prod, 2026-10-05; ~640-700 at 90 days), and a rep
		// can widen it.
		leadFinderIssuedWithin: { defaultValue: [{ id: '30', label: '30 days' }] },
	},
	columns: COLUMNS,
	defaultSortId: 'column-lead-finder-issued-desc',
	bulkActionTemplate: LEAD_FINDER_BULK_TEMPLATE,
	actions: [],
};

const allPharmaciesList: ListBuilderList = {
	id: LEAD_FINDER_LIST_IDS.ALL,
	cacheTTL: 300000,
	name: 'All pharmacies',
	type: 'default',
	active: true,
	sortOrder: 2,
	filterRules: {},
	columns: COLUMNS,
	defaultSortId: 'column-lead-finder-pharmacy-asc',
	bulkActionTemplate: LEAD_FINDER_BULK_TEMPLATE,
	actions: [],
};

const dismissedList: ListBuilderList = {
	id: LEAD_FINDER_LIST_IDS.DISMISSED,
	cacheTTL: 300000,
	name: 'Dismissed',
	type: 'default',
	active: true,
	sortOrder: 3,
	filterRules: {
		// The tab IS the dismissed set: locked and hidden, like other queue tabs.
		leadFinderLeadStatus: {
			defaultValue: { id: 'dismissed', label: 'Dismissed (not a fit)' },
			locked: true,
			hidden: true,
		},
	},
	columns: COLUMNS,
	defaultSortId: 'column-lead-finder-pharmacy-asc',
	bulkActionTemplate: LEAD_FINDER_DISMISSED_BULK_TEMPLATE,
	actions: [],
};

export const LEAD_FINDER_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [newPharmaciesList, allPharmaciesList, dismissedList],
	paging: { defaultPageSize: 50, pageSizeOptions: [25, 50, 100] },
	showResetButton: true,
	filters: {
		activeFilters: [
			'leadFinderKeywords',
			'leadFinderLeadStatus',
			'leadFinderClassOfTrade',
			'leadFinderOwnership',
			'leadFinderState',
			'leadFinderCity',
			'leadFinderZip',
			'leadFinderIssuedWithin',
		],
	},
	objects: [
		{
			baseUrl: '/lead-finder',
			objectLabel: 'Pharmacy location',
			objectTable: OBJ,
			selectedFields: [
				{ fieldName: 'name', fieldLabel: 'Pharmacy' },
				{ fieldName: 'legalName', fieldLabel: 'Legal name' },
				{ fieldName: 'addressFull', fieldLabel: 'Address' },
				{ fieldName: 'city', fieldLabel: 'City' },
				{ fieldName: 'state', fieldLabel: 'State' },
				{ fieldName: 'zip', fieldLabel: 'ZIP' },
				{ fieldName: 'phone', fieldLabel: 'Phone' },
				{ fieldName: 'classOfTradeLabel', fieldLabel: 'Class of trade' },
				{ fieldName: 'ownershipLabel', fieldLabel: 'Independent / chain' },
				{ fieldName: 'primaryLicenseNumber', fieldLabel: 'License #' },
				{ fieldName: 'newestIssueDate', fieldLabel: 'Issued' },
				{ fieldName: 'foundDate', fieldLabel: 'Found by Workd' },
				{ fieldName: 'leadStatusLabel', fieldLabel: 'Lead status' },
			],
		},
		// The Lead status column links a possible/existing client to /clients/:id.
		{ baseUrl: '/clients/:id', objectLabel: 'Client', objectTable: 'client', selectedFields: [] },
	],
};
